// import GraphQLClient from '../../adapter/GraphQLClient';
import { Chart, createMockData, ChartScopeRequest, FrequencyValue } from '@kaizenplatform/kaizen-chartjs';
import sleep from '../../utility/sleep';
import { DateTime } from 'luxon';
import GraphQLClient from '../../adapter/GraphQLClient';
import LiftReport from '../../entity/LiftReport';
import gql from 'graphql-tag';
import { OrganizationId } from '@kaizenplatform/intelligence-entity';
import Goal from '../../entity/Goal';
import UpdateLiftReport from '../../entity/UpdateLiftReport';
import CreateLiftReportInput from '../../entity/CreateLiftReportInput';
import RemoveLiftReportInput from '../../entity/RemoveLiftReportInput';

class LiftReportWebApi {
  private readonly liftReportGraphqlClient: GraphQLClient;
  private readonly dataPlatformGraphqlClient: GraphQLClient;

  constructor({
    liftReportGraphqlClient,
    dataPlatformGraphqlClient,
  }: {
    liftReportGraphqlClient: GraphQLClient;
    dataPlatformGraphqlClient: GraphQLClient;
  }) {
    this.liftReportGraphqlClient = liftReportGraphqlClient;
    this.dataPlatformGraphqlClient = dataPlatformGraphqlClient;
  }

  public liftUpReportStub = async (): Promise<Chart[]> => {
    await sleep(2000);
    return createMockData(DateTime.fromFormat('20200101', 'yyyyMMdd'), DateTime.fromFormat('20200131', 'yyyyMMdd'));
  };

  public coverageReportStub = async (): Promise<Chart[]> => {
    await sleep(2000);
    return createMockData(DateTime.fromFormat('20200101', 'yyyyMMdd'), DateTime.fromFormat('20200131', 'yyyyMMdd'));
  };

  public indexReportStub = async (): Promise<Chart[]> => {
    await sleep(2000);
    return createMockData(DateTime.fromFormat('20200101', 'yyyyMMdd'), DateTime.fromFormat('20200131', 'yyyyMMdd'));
  };

  public getLiftReports = async (organizationId: OrganizationId): Promise<LiftReport[]> => {
    const result = await this.liftReportGraphqlClient.query(
      gql`
        query liftReports($organizationId: ID!) {
          liftReports(organizationId: $organizationId) {
            id
            organization {
              id
              name
            }
            goal {
              id
              title
              triggerType
              cvValue
              cvValueCurrency
            }
            charts {
              id
              type
              state
              updatedAt
              from
              to
            }
            enabled
            mainGoal
            activityPeriodFrom
            activityPeriodTo
          }
        }
      `,
      { organizationId },
    );

    return result.liftReports.map((liftReport: LiftReport) => LiftReport.fromJSON(liftReport));
  };

  public getGoals = async (organizationId: OrganizationId): Promise<Goal[]> => {
    const result = await this.liftReportGraphqlClient.query(
      gql`
        query goals($organizationId: ID!) {
          goals(organizationId: $organizationId) {
            id
            title
            triggerType
            liftReport
            cvValue
            cvValueCurrency
          }
        }
      `,
      { organizationId },
    );

    return result.goals.map((goal: Goal) => Goal.fromJSON(goal));
  };

  public getLiftReport = async (organizationId: OrganizationId): Promise<LiftReport> => {
    const result = await this.liftReportGraphqlClient.query(
      gql`
        query liftReport($id: ID!) {
          liftReport(id: $id) {
            id
            organization {
              id
              name
            }
            goal {
              id
              title
              triggerType
              cvValue
              cvValueCurrency
            }
            chart {
              id
            }
            enabled
            mainGoal
            activityPeriodFrom
            activityPeriodTo
          }
        }
      `,
      { organizationId },
    );

    return LiftReport.fromJSON(result.liftReport);
  };

  public getLiftReportFrequency = async (
    id: number,
    scope: ChartScopeRequest,
    breakdowns?: any[],
  ): Promise<FrequencyValue[]> => {
    const result = await this.dataPlatformGraphqlClient.query(
      gql`
        query chart($id: ID!, $scope: ChartScopeInput!, $breakdowns: [ChartBreakdownInput!]) {
          chart(id: $id, scope: $scope, breakdowns: $breakdowns) {
            frequencies
          }
        }
      `,
      { id, scope, breakdowns },
    );
    return result.chart.frequencies;
  };

  public getLiftReportChart = async (id: number, scope: ChartScopeRequest, breakdowns?: any[]): Promise<Chart> => {
    const result = await this.dataPlatformGraphqlClient.query(
      gql`
        query chart($id: ID!, $scope: ChartScopeInput!, $breakdowns: [ChartBreakdownInput!]) {
          chart(id: $id, scope: $scope, breakdowns: $breakdowns) {
            id
            header {
              breakdowns {
                breakdown
                filter
              }
              scope {
                frequency
                from
                to
              }
            }
            label
            columns
            structure {
              datasets {
                id
                type
                label
                rows {
                  columns
                  value
                  breakdown
                }
                color
                position
                percentage
              }
              originalDatasets {
                id
                type
                label
              }
            }
            breakdowns {
              key {
                label
                value
              }
              display
              filterRequired
              values
            }
            frequencies
          }
        }
      `,
      { id, scope, breakdowns },
    );
    return Chart.fromJSON(result.chart);
  };

  public updateLiftreportPeriod = async (input: UpdateLiftReport): Promise<LiftReport> => {
    const result = await this.liftReportGraphqlClient.query(
      gql`
        mutation updateLiftReport($input: UpdateLiftReportInput!) {
          updateLiftReport(input: $input) {
            id
            organization {
              id
              name
            }
            goal {
              id
              title
              triggerType
              cvValue
              cvValueCurrency
            }
            enabled
            mainGoal
            activityPeriodFrom
            activityPeriodTo
          }
        }
      `,
      { input },
    );

    return LiftReport.fromJSON(result.updateLiftReport);
  };

  public createLiftreportPeriod = async (input: CreateLiftReportInput): Promise<LiftReport> => {
    const result = await this.liftReportGraphqlClient.query(
      gql`
        mutation createLiftReport($input: CreateLiftReportInput!) {
          createLiftReport(input: $input) {
            id
            organization {
              id
              name
            }
            goal {
              id
              title
              triggerType
              cvValue
              cvValueCurrency
            }
            enabled
            mainGoal
            activityPeriodFrom
            activityPeriodTo
          }
        }
      `,
      { input },
    );

    return LiftReport.fromJSON(result.createLiftReport);
  };

  public removeLiftreportPeriod = async (input: RemoveLiftReportInput): Promise<void> => {
    await this.liftReportGraphqlClient.query(
      gql`
        mutation removeLiftReport($input: CreateLiftReportInput!) {
          createLiftReport(input: $input) {}
      `,
      { input },
    );
  };
}

export default LiftReportWebApi;
